import { usePostHog } from 'posthog-js/react';
import { useMemo } from 'react';

import { Environment } from '../constants/environments';
import { ResalePaymentTracking } from '../trackings/PostHog/ResalePaymentTracking';
import { ResaleTracking } from '../trackings/PostHog/ResaleTracking';

/**
 * useResaleTracking - Custom hook for PostHog tracking in resale flow
 *
 * Provides access to type-safe tracking classes for resale and payment pages.
 * Automatically handles PostHog initialization and debug mode based on environment.
 *
 * @returns Object with resaleTracking and paymentTracking instances
 *
 * @example
 * ```tsx
 * const { resaleTracking, paymentTracking } = useResaleTracking();
 *
 * // Track page view
 * resaleTracking.trackPageView(resaleData, event, user, authenticatedUser);
 *
 * // Track payment started
 * paymentTracking.trackCheckoutRequestStarted(resaleData, event, user, document);
 * ```
 */
export const useResaleTracking = () => {
  const posthog = usePostHog();

  // Enable debug mode in development/homologation
  const isDebugMode = Environment.NODE_ENV === 'development' ||
    Environment.ENVIRONMENT === 'homologation';

  // Memoize tracking instances to avoid recreation on every render
  const resaleTracking = useMemo(
    () => new ResaleTracking(posthog, isDebugMode),
    [posthog, isDebugMode]
  );

  const paymentTracking = useMemo(
    () => new ResalePaymentTracking(posthog, isDebugMode),
    [posthog, isDebugMode]
  );

  return {
    resaleTracking,
    paymentTracking,
  };
};
